// Money + date formatting. Money always arrives as integer minor units + an ISO
// currency (never floats); we format for display only and never do arithmetic on the
// formatted string.

const MINOR_DIGITS: Record<string, number> = { JPY: 0, KRW: 0, CLP: 0, ISK: 0 };

function minorDigits(currency: string): number {
  return MINOR_DIGITS[currency?.toUpperCase()] ?? 2;
}

/** Format integer minor units + currency, e.g. (25000,'USD') -> "$250.00". */
export function formatMoney(minor: string | number | null | undefined, currency = 'USD'): string {
  if (minor === null || minor === undefined || minor === '') return '—';
  const digits = minorDigits(currency);
  const value = Number(minor) / 10 ** digits;
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(value);
  } catch {
    return `${value.toFixed(digits)} ${currency}`;
  }
}

/** Alias kept for existing call sites; identical to formatMoney. */
export const formatMinor = formatMoney;

/**
 * Integer minor units (the API sends bigint-as-string) -> a JS integer, for sums/sorts
 * only. This is the ONE sanctioned conversion in the SPA (lint: hf/no-float-money);
 * anything past Number.MAX_SAFE_INTEGER throws rather than silently losing cents.
 */
export function toMinorInt(minor: string | number | bigint | null | undefined): number {
  if (minor === null || minor === undefined || minor === '') return 0;
  const n = typeof minor === 'bigint' ? Number(minor) : Number(minor);
  if (!Number.isSafeInteger(n)) throw new RangeError(`minor amount out of safe integer range: ${String(minor)}`);
  return n;
}

/** Sum a minor-unit field across rows with integer arithmetic (no floats, no /100). */
export function sumMinor<T>(rows: readonly T[], pick: (row: T) => string | number | bigint | null | undefined): number {
  return rows.reduce((s, r) => s + toMinorInt(pick(r)), 0);
}

/** Sort comparator for minor amounts (ascending); wrap with Math.abs or negate for other orders. */
export function compareMinor(
  a: string | number | bigint | null | undefined,
  b: string | number | bigint | null | undefined,
): number {
  return toMinorInt(a) - toMinorInt(b);
}

/** Sign of a minor amount: 'pos' | 'neg' | 'zero' — drives money coloring. */
export function moneySign(minor: string | number | null | undefined): 'pos' | 'neg' | 'zero' {
  const n = Number(minor ?? 0);
  if (n > 0) return 'pos';
  if (n < 0) return 'neg';
  return 'zero';
}

/** Format a numeric quantity (hours etc.); trims trailing zeros. */
export function formatQty(qty: string | number | null | undefined): string {
  if (qty === null || qty === undefined || qty === '') return '—';
  const n = Number(qty);
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

/** ISO date/timestamp -> short local date, e.g. "Aug 6, 2026". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** ISO timestamp -> date + time, e.g. "Aug 6, 2026, 14:03". */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Parse a human money string in MAJOR units ("1,000.50", "$250", "1250") into integer
 * minor units for the given currency. Returns null for empty/invalid input — callers
 * treat null as "not a valid amount", never 0.
 */
export function parseMoneyToMinor(input: string, currency = 'USD'): number | null {
  const cleaned = input.replace(/[^0-9.,-]/g, '').replace(/,/g, '');
  if (!cleaned || cleaned === '-' || cleaned === '.') return null;
  const major = Number(cleaned);
  if (!Number.isFinite(major)) return null;
  const minor = Math.round(major * 10 ** minorDigits(currency));
  if (!Number.isSafeInteger(minor)) return null;
  return minor;
}

/** Minor units -> a plain major-units string for editing (no symbol/grouping), e.g. 25000 -> "250.00". */
export function minorToMajorString(minor: number | string | null | undefined, currency = 'USD'): string {
  if (minor === null || minor === undefined || minor === '') return '';
  const digits = minorDigits(currency);
  return (Number(minor) / 10 ** digits).toFixed(digits);
}

/** Calendar-day distance from today -> "today" / "in 3 days" / "5 days ago"; null when unparseable. */
export function formatRelativeDays(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const utcDay = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
  const diff = Math.round((utcDay(d) - utcDay(new Date())) / 86_400_000);
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  if (diff === -1) return 'yesterday';
  return diff > 0 ? `in ${diff} days` : `${-diff} days ago`;
}

/**
 * Parse human duration input into minutes: "90" (minutes), "90m", "1:30", "1.5h", "2h".
 * Returns null for empty/invalid input.
 */
export function parseDurationToMinutes(input: string): number | null {
  const s = input.trim().toLowerCase();
  if (!s) return null;
  const hm = s.match(/^(\d+):([0-5]?\d)$/);
  if (hm) return Number(hm[1]) * 60 + Number(hm[2]);
  const h = s.match(/^(\d+(?:\.\d+)?)\s*h(?:ours?|rs?)?$/);
  if (h) return Math.round(Number(h[1]) * 60);
  const m = s.match(/^(\d+)\s*m(?:in(?:ute)?s?)?$/);
  if (m) return Number(m[1]);
  if (/^\d+$/.test(s)) return Number(s);
  return null;
}

/** Minutes -> "2h 30m" / "45m". */
export function formatMinutes(mins: number | string | null | undefined): string {
  const n = Number(mins ?? 0);
  if (!n) return '0m';
  const h = Math.floor(n / 60);
  const m = n % 60;
  return h ? (m ? `${h}h ${m}m` : `${h}h`) : `${m}m`;
}
